import type {FieldValue} from "./Field.js";
import type {FieldMap, JsonValue} from "./types.js";

/**
 * Compact filter serialization: a tree of `{o, f1, v}` leaves joined by
 * `{o: "and" | "or", l, r}` nodes, sent to the backend in the `Where` header.
 */
export type FilterJson =
    | { o: "and" | "or"; l: FilterJson; r: FilterJson }
    | { o: string; f1: string; v: JsonValue };

export type ComparisonOperator = "=" | "!=" | "~" | "=~" | ">" | "<" | ">=" | "<=";
export type ListOperator = "in" | "!in";
/** Operators that compare a part of a date rather than the date itself. */
export type DatePartOperator = "year" | "month";

/** Resolves a field path to the value the entity currently holds. */
export type ValueReader = (path: string) => unknown;

const serializeValue = (value: unknown): JsonValue => {
    if (value instanceof Date) {
        return value.toISOString();
    }
    if (Array.isArray(value)) {
        return value.map(serializeValue);
    }
    if (value === undefined) {
        return null;
    }
    if (typeof value === "object" && value !== null && "id" in value) {
        return (value as { id: JsonValue }).id;
    }
    return value as JsonValue;
};

const compare = (left: JsonValue, right: JsonValue): number => {
    if (typeof left === "number" && typeof right === "number") {
        return left - right;
    }
    return String(left).localeCompare(String(right));
};

const matchesLeaf = (operator: string, actual: unknown, expected: JsonValue): boolean => {
    const value = serializeValue(actual);
    switch (operator) {
        case "year":
        case "month": {
            const date = actual instanceof Date ? actual : new Date(String(value));
            if (Number.isNaN(date.getTime())) {
                return false;
            }
            return expected === (operator === "year" ? date.getFullYear() : date.getMonth() + 1);
        }
        case "=":
            return value === expected;
        case "!=":
            return value !== expected;
        case "~":
            return String(value ?? "").toLowerCase().includes(String(expected ?? "").toLowerCase());
        case "=~":
            return String(value ?? "").toLowerCase().startsWith(String(expected ?? "").toLowerCase());
        case "in":
            return Array.isArray(expected) && expected.includes(value);
        case "!in":
            return Array.isArray(expected) && !expected.includes(value);
        case ">":
            return compare(value, expected) > 0;
        case "<":
            return compare(value, expected) < 0;
        case ">=":
            return compare(value, expected) >= 0;
        case "<=":
            return compare(value, expected) <= 0;
        default:
            throw new Error(`Unsupported filter operator "${operator}"`);
    }
};

const isBranch = (json: FilterJson): json is { o: "and" | "or"; l: FilterJson; r: FilterJson } =>
    json.o === "and" || json.o === "or";

/**
 * A query over one model: serializable for the backend, and testable locally against
 * entities the client already holds.
 */
export class Filter {
    constructor(readonly json: FilterJson) {
    }

    and(other: Filter): Filter {
        return new Filter({o: "and", l: this.json, r: other.json});
    }

    or(other: Filter): Filter {
        return new Filter({o: "or", l: this.json, r: other.json});
    }

    /** Joins filters with `and`, or nothing at all when the list is empty. */
    static all(filters: readonly Filter[]): Filter | undefined {
        return filters.reduce<Filter | undefined>((joined, filter) => joined?.and(filter) ?? filter, undefined);
    }

    /** Joins filters with `or`, or nothing at all when the list is empty. */
    static any(filters: readonly Filter[]): Filter | undefined {
        return filters.reduce<Filter | undefined>((joined, filter) => joined?.or(filter) ?? filter, undefined);
    }

    /** Evaluates the filter without a round trip, using the same semantics as the backend. */
    test(read: ValueReader): boolean {
        return test(this.json, read);
    }
}

const test = (json: FilterJson, read: ValueReader): boolean => {
    if (isBranch(json)) {
        return json.o === "and"
            ? test(json.l, read) && test(json.r, read)
            : test(json.l, read) || test(json.r, read);
    }
    return matchesLeaf(json.o, read(json.f1), json.v);
};

/** Filters addressed by path: the part of a filter builder that needs no field map. */
export interface PathFilters {
    path(path: string, operator: ComparisonOperator | ListOperator | DatePartOperator, value: unknown): Filter;
}

/**
 * Builds filters whose field names and value types come from the model, so a typo or a
 * mistyped comparison fails at compile time instead of on the server.
 */
export class FilterBuilder<F extends FieldMap> implements PathFilters {
    constructor(private readonly fieldNames: ReadonlySet<string>) {
    }

    where<K extends keyof F & string>(path: K, operator: ComparisonOperator, value: FieldValue<F[K]>): Filter {
        return this.#leaf(path, operator, value);
    }

    eq<K extends keyof F & string>(path: K, value: FieldValue<F[K]>): Filter {
        return this.#leaf(path, "=", value);
    }

    ne<K extends keyof F & string>(path: K, value: FieldValue<F[K]>): Filter {
        return this.#leaf(path, "!=", value);
    }

    contains<K extends keyof F & string>(path: K, value: string): Filter {
        return this.#leaf(path, "~", value);
    }

    startsWith<K extends keyof F & string>(path: K, value: string): Filter {
        return this.#leaf(path, "=~", value);
    }

    oneOf<K extends keyof F & string>(path: K, values: readonly FieldValue<F[K]>[], operator: ListOperator = "in"): Filter {
        return this.#leaf(path, operator, values);
    }

    /** Escape hatch for paths through relations, such as `app.code`, which no field map can enumerate. */
    path(path: string, operator: ComparisonOperator | ListOperator | DatePartOperator, value: unknown): Filter {
        return new Filter({o: operator, f1: path, v: serializeValue(value)});
    }

    /** Matches the calendar year of a date field, which the backend extracts in SQL. */
    year<K extends keyof F & string>(path: K, year: number): Filter {
        return this.#leaf(path, "year", year);
    }

    /** Matches the calendar month of a date field, 1 for January. */
    month<K extends keyof F & string>(path: K, month: number): Filter {
        return this.#leaf(path, "month", month);
    }

    #leaf(path: string, operator: string, value: unknown): Filter {
        if (!this.fieldNames.has(path)) {
            throw new Error(`Unknown field "${path}"`);
        }
        return new Filter({o: operator, f1: path, v: serializeValue(value)});
    }
}
